import { useState, useCallback, useMemo } from "react";
import { ReactFlow, Controls, Background, type Node, type Edge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { TriggerNode } from "./TriggerNode";
import { ActionNode } from "./ActionNode";
import { AddNode } from "./AddNode";
import { WorkflowPropertyPanel } from "./WorkflowPropertyPanel";
import { type WorkflowFormData, TRIGGER_TYPES, ACTION_TYPES } from "./constants";

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  add: AddNode,
};

interface FlowBuilderProps {
  formData: WorkflowFormData;
  setFormData: (data: WorkflowFormData) => void;
}

export function FlowBuilder({ formData, setFormData }: FlowBuilderProps) {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Compute nodes and edges from formData
  const { nodes, edges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // Trigger Node
    nodes.push({
      id: "trigger",
      type: "trigger",
      position: { x: 250, y: 50 },
      data: {
        triggerType: formData.triggerType,
        label: TRIGGER_TYPES.find((t) => t.value === formData.triggerType)?.label,
      },
    });

    let yPos = 200;

    // Action Nodes
    formData.actions.forEach((action, index) => {
      const nodeId = `action-${index}`;
      nodes.push({
        id: nodeId,
        type: "action",
        position: { x: 250, y: yPos },
        data: {
          actionType: action.type,
          label: ACTION_TYPES.find((t) => t.value === action.type)?.label,
          description: ACTION_TYPES.find((t) => t.value === action.type)?.description,
        },
      });

      // Edge from previous node
      const prevNodeId = index === 0 ? "trigger" : `action-${index - 1}`;
      edges.push({
        id: `e-${prevNodeId}-${nodeId}`,
        source: prevNodeId,
        target: nodeId,
        type: "default",
        animated: true,
      });

      yPos += 150;
    });

    // Add Button Node
    const addNodeId = "add-button";
    nodes.push({
      id: addNodeId,
      type: "add",
      position: { x: 250, y: yPos },
      data: {
        onAdd: () => {
          setFormData({
            ...formData,
            actions: [...formData.actions, { type: "unlock_pdf" }],
          });
        },
      },
    });

    // Edge to Add Button
    const lastActionId =
      formData.actions.length > 0 ? `action-${formData.actions.length - 1}` : "trigger";
    edges.push({
      id: `e-${lastActionId}-${addNodeId}`,
      source: lastActionId,
      target: addNodeId,
      type: "default",
      animated: true,
      style: { strokeDasharray: "5,5" },
    });

    return { nodes, edges };
  }, [formData, setFormData]);

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (node.type === "add") return;
    setSelectedNodeId(node.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const nodesWithSelection = useMemo(() => {
    return nodes.map((node) => ({
      ...node,
      selected: node.id === selectedNodeId,
    }));
  }, [nodes, selectedNodeId]);

  const onDeleteAction = (index: number) => {
    const newActions = formData.actions.filter((_, i) => i !== index);
    setFormData({ ...formData, actions: newActions });
    setSelectedNodeId(null);
  };

  return (
    <div className="flex h-full w-full bg-muted overflow-hidden rounded-lg border border-border">
      <div className="flex-1 h-full relative">
        <ReactFlow
          nodes={nodesWithSelection}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          fitView
          minZoom={0.5}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#e2e8f0" gap={20} size={1} />
          <Controls />
        </ReactFlow>
      </div>
      {selectedNodeId && (
        <div className="border-l border-border bg-background h-full shadow-xl z-10 w-[400px]">
          <WorkflowPropertyPanel
            nodeId={selectedNodeId}
            formData={formData}
            setFormData={setFormData}
            onClose={() => setSelectedNodeId(null)}
            onDeleteAction={selectedNodeId.startsWith("action-") ? onDeleteAction : undefined}
          />
        </div>
      )}
    </div>
  );
}
